import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AppHeader } from "@/components/Layout";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { renderMarkdown, imageUrl, formatDate } from "@/lib/blog";

/**
 * Staff-only draft preview reachable at /blog/preview/:token. The token is unguessable
 * (secrets.token_urlsafe) and never indexed because this page sets noindex headers.
 */
export default function BlogPreview() {
  const { token } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    document.title = "Draft preview | Food Recall Alerts";
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    api.get(`/blog/preview/${token}`).then(setPost).catch((e) => toast.error(errMsg(e)));
    return () => meta.remove();
  }, [token]);

  if (!post) return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <p className="mx-auto max-w-xl p-10 text-slate-500">Loading preview...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="sticky top-0 z-30 border-y border-amber-200 bg-amber-50 px-5 py-2 text-xs font-semibold text-amber-900" data-testid="preview-banner">
        DRAFT PREVIEW — not published, not indexed by search engines.
      </div>
      <article className="mx-auto max-w-3xl px-5 py-10" data-testid="preview-body">
        {post.hero_image && (
          <img src={imageUrl(post.hero_image)} alt={post.hero_alt || ""} className="aspect-[16/9] w-full rounded-2xl object-cover" />
        )}
        <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900 sm:text-5xl">{post.title}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {post.author?.name && `By ${post.author.name} · `}{formatDate(post.publish_date || post.updated_at)}
        </p>
        <div className="prose prose-slate mt-6 max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }} />
      </article>
    </div>
  );
}
